import {
  CalendarDate,
  CalendarDateTime,
  parseDate,
  parseDateTime,
  parseTime,
  Time,
} from "@internationalized/date";
import { format, getUnixTime, isValid } from "date-fns";
import { DateRangePickerProps } from "ds/ui";

export const toCalendarDate = (
  value: number | Date | CalendarDate | CalendarDateTime | null | undefined,
  granularity?: string,
): CalendarDate | CalendarDateTime | undefined => {
  if (!value) return undefined;

  // If it's a unix timestamp, convert it to a Date object
  if (typeof value === "number") {
    value = new Date(value * 1000);
  }

  // If it's already a CalendarDate or CalendarDateTime, return it
  if (
    typeof value === "object" &&
    "year" in value &&
    "month" in value &&
    "day" in value
  ) {
    return value;
  }

  if (isValid(value)) {
    const isTimeGranularity =
      granularity === "minute" || granularity === "second";
    const formatString = isTimeGranularity
      ? "yyyy-MM-dd'T'HH:mm:ss"
      : "yyyy-MM-dd";
    const formatted = format(value, formatString);
    return isTimeGranularity ? parseDateTime(formatted) : parseDate(formatted);
  }

  // Invalid value
  return undefined;
};

export const toTime = (
  value: Date | Time | null | undefined,
): Time | undefined => {
  if (!value) return undefined;

  // If it's already a Time object, return it
  if ("hour" in value && "minute" in value) {
    return value as Time;
  }

  // If it's a Date object, convert it
  const formattedTime = format(value as Date, "HH:mm:ss");
  return parseTime(formattedTime);
};

export const convertCalendarDateToUnix = (
  calendarDate: CalendarDate | CalendarDateTime | null,
  granularity: DateRangePickerProps["granularity"] = "day",
): number | null => {
  if (!calendarDate) return null;

  if (granularity === "minute" || granularity === "second") {
    const dateTime = calendarDate as CalendarDateTime;
    return getUnixTime(
      new Date(
        dateTime.year,
        dateTime.month - 1,
        dateTime.day,
        dateTime.hour,
        dateTime.minute,
        dateTime.second ?? 0,
      ),
    );
  }

  return getUnixTime(
    new Date(
      calendarDate.year,
      calendarDate.month - 1,
      calendarDate.day,
      12,
      0,
    ),
  );
};

export const convertCalendarRangeToUnixRange = (
  range: {
    end: CalendarDate | CalendarDateTime | null;
    start: CalendarDate | CalendarDateTime | null;
  } | null,
  granularity: DateRangePickerProps["granularity"] = "day",
): { end: number | null; start: number | null } => {
  if (!range) {
    return { start: null, end: null };
  }

  return {
    start: convertCalendarDateToUnix(range.start, granularity),
    end: convertCalendarDateToUnix(range.end, granularity),
  };
};
